import React from 'react'
import { connect } from 'react-redux'
import _ from 'lodash'

import { Header1, Body1 } from 'pharmacy/src/typography'
import { Button } from 'pharmacy/src/input/button'
import { StarLoader } from 'pharmacy/src/misc/loaders/starLoader'
import { Logo } from 'pharmacy/src/style/logo'

import { fetchOnboardingTopics } from 'mednet-cns/src/reducers/specialty'
import { replaceTopicFollowings } from 'mednet-cns/src/reducers/topic'
import { finishOnboarding } from 'mednet-cns/src/reducers/user'

import { mixpanel } from 'mednet-util/src/tracking'

import TopicCheckbox from './topicCheckbox'

import css from './customizeFeedScreen.scss'

// Followed topics first; then the specialty itself, the specialty's other
// topics and its subspecialties' topics; then by name.
const orderOnboardingTopics = ({
  specialtyMainTopicId,
  specialtyTopicIds,
  topics,
}) =>
  _.orderBy(
    topics,
    [
      'isFollowed',
      (topic) => topic.topicId === specialtyMainTopicId,
      (topic) => specialtyTopicIds.includes(topic.topicId),
      'topic',
    ],
    ['desc', 'desc', 'desc', 'asc']
  )

// Lists the topics of the member's specialty and its onboarding subspecialties,
// with the ones they follow ticked. Done makes the ticked topics their topic
// follows.
class CustomizeFeedScreen extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      topics: null,
      loadFailed: false,
      selectedTopicIds: [],
      isSaving: false,
      errorMessage: null,
    }
  }

  componentDidMount() {
    this.fetchTopics()
  }

  fetchTopics = () => {
    const { specialtyId } = this.props
    if (specialtyId) {
      this.setState({ loadFailed: false })
      this.props.fetchOnboardingTopics(
        specialtyId,
        this.handleTopicsReceived,
        () => this.setState({ loadFailed: true })
      )
    } else {
      // Without a specialty there are no topics to offer.
      this.setState({ topics: [] })
    }
  }

  handleTopicsReceived = (response) => {
    // Ids are made numbers so they compare equal whether they arrive as
    // numbers or strings.
    const topics = orderOnboardingTopics({
      specialtyMainTopicId: Number(response.specialtyMainTopicId),
      specialtyTopicIds: _.map(response.specialtyTopicIds, Number),
      topics: response.topics.map((topic) => ({
        ...topic,
        topicId: Number(topic.topicId),
      })),
    })
    this.setState({
      topics,
      selectedTopicIds: _.map(_.filter(topics, 'isFollowed'), 'topicId'),
    })
  }

  handleToggle = (topicId) => {
    this.setState(({ selectedTopicIds }) => ({
      selectedTopicIds: _.xor(selectedTopicIds, [topicId]),
      errorMessage: null,
    }))
  }

  finishAndClose = () => {
    this.props.finishOnboarding()
    this.props.closeModal()
  }

  handleClose = () => {
    // Nothing was listed, so there is no selection to save.
    if (!this.props.specialtyId || this.state.loadFailed) {
      this.finishAndClose()
      return
    }

    const { selectedTopicIds } = this.state
    this.setState({ isSaving: true, errorMessage: null })
    this.props.replaceTopicFollowings(
      selectedTopicIds,
      () => {
        mixpanel.track('selected_onboarding_topics', {
          topics: selectedTopicIds,
        })
        this.finishAndClose()
      },
      () =>
        this.setState({
          isSaving: false,
          errorMessage: 'Failed to update your preferences. Please try again.',
        })
    )
  }

  render() {
    const { topics, loadFailed, selectedTopicIds, isSaving, errorMessage } =
      this.state
    if (!topics && !loadFailed) {
      return (
        <StarLoader
          wait={false}
          className={css.loading}
          isVerticalMargin={false}
        />
      )
    }

    return (
      <div className={css.container}>
        <div className={css.logoContainer}>
          <Logo className={css.logo} />
        </div>

        <div className={css.contentWrapper}>
          <div className={css.headerWrapper}>
            <div className={css.header}>
              <Header1 className={css.title}>Customize your feed</Header1>
              <Body1 className={css.subtitle}>
                Select topics to follow for a personalized feed.
              </Body1>
            </div>
          </div>

          {loadFailed ? (
            <div className={css.loadError}>
              <Body1 className={css.loadErrorMessage}>
                Couldn&apos;t load topics.
              </Body1>
              <Button type="secondary" onClick={this.fetchTopics}>
                Try again
              </Button>
            </div>
          ) : (
            <div className={css.topicList}>
              {topics.map((topic) => (
                <TopicCheckbox
                  key={topic.topicId}
                  topic={topic}
                  checked={selectedTopicIds.includes(topic.topicId)}
                  onChange={() => this.handleToggle(topic.topicId)}
                />
              ))}
            </div>
          )}
        </div>

        <div className={css.buttonContainer}>
          {errorMessage && (
            <Body1 className={css.errorMessage}>{errorMessage}</Body1>
          )}
          <Button
            className={css.continueButton}
            onClick={this.handleClose}
            isLoading={isSaving}
          >
            Done
          </Button>
        </div>
      </div>
    )
  }
}

const mapStateToProps = (state) => {
  return {
    specialtyId: state.user?.data?.specialtyId,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    fetchOnboardingTopics: (specialtyId, callback, errorCallback) =>
      dispatch(fetchOnboardingTopics(specialtyId, callback, errorCallback)),
    replaceTopicFollowings: (topicIds, callback, errorCallback) =>
      dispatch(replaceTopicFollowings(topicIds, callback, errorCallback)),
    finishOnboarding: () => dispatch(finishOnboarding()),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(CustomizeFeedScreen)
